import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Loader2, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditChat({ placeId, businessName, hasAccess, onUnlock }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: `Hi! I'm Alex from RankifyLocal. I have your full audit data for ${businessName || "your business"} right here. Ask me anything about your results — scores, issues, what to fix first, competitors, anything.`,
      }]);
    }
  }, [open, businessName, messages.length]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const q = input.trim();
    if (!q || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat/ask`, {
        place_id: placeId,
        question: q,
        session_id: sessionId,
      });
      if (res.data.session_id && !sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.answer }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating button when closed
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="chat-fab"
        style={{
          position: "fixed",
          bottom: 70,
          right: 20,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: hasAccess ? "#3B5BDB" : "#6B7280",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: hasAccess ? "0 4px 20px rgba(59,91,219,0.35)" : "0 4px 20px rgba(0,0,0,0.15)",
          zIndex: 60,
          transition: "transform 0.2s",
        }}
        onMouseOver={e => e.currentTarget.style.transform = "scale(1.08)"}
        onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
      >
        {hasAccess ? (
          <MessageCircle style={{ width: 22, height: 22 }} />
        ) : (
          <Lock style={{ width: 20, height: 20 }} />
        )}
      </button>
    );
  }

  // Locked state panel for unpaid users
  if (!hasAccess) {
    return (
      <div
        data-testid="audit-chat-locked"
        style={{
          position: "fixed",
          bottom: 70,
          right: 20,
          width: 340,
          maxWidth: "calc(100vw - 40px)",
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #E5E7EB",
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
          zIndex: 60,
          overflow: "hidden",
          padding: "28px 24px",
          textAlign: "center",
        }}
      >
        <button
          onClick={() => setOpen(false)}
          style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", cursor: "pointer", padding: 4 }}
        >
          <X style={{ width: 16, height: 16, color: "#6B7280" }} />
        </button>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Lock style={{ width: 22, height: 22, color: "#6B7280" }} />
        </div>
        <p style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 6 }}>Ask Alex — AI Audit Consultant</p>
        <p style={{ fontSize: 13, color: "#6B7280", marginBottom: 20, lineHeight: 1.5 }}>
          Unlock your full report to chat with Alex about your audit results, get personalized fix recommendations, and ask follow-up questions.
        </p>
        <button
          onClick={() => { setOpen(false); onUnlock?.(); }}
          data-testid="chat-unlock-btn"
          style={{
            width: "100%",
            padding: "12px 20px",
            borderRadius: 10,
            background: "#111827",
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
            border: "none",
            cursor: "pointer",
          }}
        >
          Unlock Full Report — $27
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="audit-chat-panel"
      style={{
        position: "fixed",
        bottom: 70,
        right: 20,
        width: 380,
        maxWidth: "calc(100vw - 40px)",
        height: 480,
        maxHeight: "calc(100vh - 140px)",
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #E5E7EB",
        boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
        zIndex: 60,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "14px 16px",
        borderBottom: "1px solid #E5E7EB",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#F9FAFB",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#3B5BDB", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MessageCircle style={{ width: 16, height: 16, color: "#fff" }} />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: 0 }}>Ask Alex</p>
            <p style={{ fontSize: 11, color: "#6B7280", margin: 0 }}>AI Audit Consultant</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="chat-close-btn"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
        >
          <X style={{ width: 18, height: 18, color: "#6B7280" }} />
        </button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}>
        {messages.map((msg, i) => (
          <div
            key={i}
            data-testid={`chat-msg-${msg.role}-${i}`}
            style={{
              maxWidth: "85%",
              alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
              background: msg.role === "user" ? "#3B5BDB" : "#F3F4F6",
              color: msg.role === "user" ? "#fff" : "#111827",
              borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
              padding: "10px 14px",
              fontSize: 13,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
            }}
          >
            {msg.content}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#F3F4F6", borderRadius: "14px 14px 14px 4px" }}>
            <Loader2 style={{ width: 14, height: 14, color: "#6B7280", animation: "spin 1s linear infinite" }} />
            <span style={{ fontSize: 12, color: "#6B7280" }}>Thinking...</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{
        borderTop: "1px solid #E5E7EB",
        padding: "10px 12px",
        display: "flex",
        gap: 8,
        background: "#fff",
      }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your audit..."
          data-testid="chat-input"
          style={{
            flex: 1,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #E5E7EB",
            fontSize: 13,
            outline: "none",
            color: "#111827",
          }}
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || loading}
          data-testid="chat-send-btn"
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: input.trim() && !loading ? "#3B5BDB" : "#E5E7EB",
            color: "#fff",
            border: "none",
            cursor: input.trim() && !loading ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Send style={{ width: 16, height: 16 }} />
        </button>
      </div>
    </div>
  );
}
